import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import BetTableLucky7 from "../../casinoComponents/betTableLucky7";
import VideoBox from "../../casinoComponents/videoBox";
import MyBet from "../../casinoComponents/myBet";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import ViewMoreModal from "../../../components/ViewMoreModal";
import Lucky7CardsOverlay from "../../casinoComponents/videoBox/overlays/Lucky7CardsOverlay";
import Lucky7RefResults from "../resultui/resultLucky7/Lucky7RefResults";
import VLucky7View from "./VLucky7View";
import { getCasinoGameDetails, getMyBets } from "../../../apiservices/CasionApi";
import { extractCasinoGame } from "../../../utils/casinoGame";
import "../../../styles/games/lucky7aaa.css";

// Reference game-header titles per API type (reference: lastresults2 res1.cname)
const LUCKY7_TITLES = {
  lucky7: "Lucky 7 - A",
  plucky7: "Premium Lucky 7",
  tlucky7: "Tembo Lucky 7",
  lucky7eu: "Lucky 7 - B",
  lucky7eu2: "Lucky 7 - C",
  vlucky7: "V-Lucky 7 - A",
};

/**
 * Lucky 7 pages — casino/lucky7, casino/plucky7, casino/lucky7eu (Lucky7B), casino/lucky7eu2 (Lucky7C)
 * in the reference shell (.casino-table.lucky7), and vcasino/lucky7 (vlucky7) in the vcasino layout.
 */
export function Lucky7Page({ defaultType = "lucky7" }) {
  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || defaultType;
  const isVirtual = pathname.includes("/vcasino/");

  const [gameData, setGameData] = useState(null);
  const [myBets, setMyBets] = useState([]);
  const [exposures, setExposures] = useState({});
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  useEffect(() => {
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("Error fetching Lucky 7 game data: ", err);
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [gameName]);

  const matchId = gameData?.mid?.toString() || gameData?.data?.data?.mid?.toString() || gameData?.data?.mid?.toString();

  const fetchMyBetsData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;

      if (userId && matchId) {
        const response = await getMyBets(userId, matchId);
        if (response.success && response.bets) {
          const formattedBets = response.bets.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.userName ?? bet.username,
            placeDate: bet.placeDate ?? bet.created_at,
            matchedBet: bet.player_name || bet.selection || "",
            odds: bet.odds || "0",
            stake: bet.stake || "0",
            type: bet.type || null,
            selection: bet.selection || bet.player_name || "",
            exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
          }));
          setMyBets(formattedBets);

          // Map exposure from myBets to exposures object
          const exposureMap = {};
          response.bets.forEach((bet) => {
            const selection = bet.selection || bet.player_name || "";
            const exposureAmount = parseFloat(bet.exposer || bet.exposure_amount || "0") || 0;
            if (selection && exposureAmount !== 0) {
              exposureMap[selection] = exposureAmount;
              exposureMap[selection.trim()] = exposureAmount;
              exposureMap[selection.toLowerCase().trim()] = exposureAmount;
              exposureMap[selection.toLowerCase()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching my bets:", error);
    }
  }, [matchId]);

  useEffect(() => {
    if (!matchId) return;

    const timeoutId = setTimeout(fetchMyBetsData, 2000);
    const intervalId = setInterval(fetchMyBetsData, 2000);
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [fetchMyBetsData, matchId]);

  // Admin panel places no bets (the old PlaceBet was a no-op stub): an odds click refreshes book + bets
  const handleBetClick = () => {
    fetchMyBetsData();
  };

  const feed = gameData?.data?.data ?? gameData?.data ?? {};
  const sub = Array.isArray(feed?.sub) ? feed.sub : [];
  const roundId = feed?.mid || gameData?.mid;
  const title = LUCKY7_TITLES[gameName] || LUCKY7_TITLES.lucky7;
  const resultData = gameData?.lrs ?? feed?.lrs ?? [];
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const viewMoreModal = (
    <ViewMoreModal
      show={showViewMoreModal}
      onHide={() => setShowViewMoreModal(false)}
      title="View More"
      sectionKey={gameName}
    />
  );

  if (isVirtual) {
    return (
      <VLucky7View
        gameName={gameName}
        title={title}
        data={feed}
        exposures={exposures}
        onBetClick={handleBetClick}
        myBets={myBets}
        onViewMore={() => setShowViewMoreModal(true)}
        viewMoreModal={viewMoreModal}
      />
    );
  }

  return (
    <CasinoTableLayout
      tableClass="lucky7"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading name={title} roundId={roundId || ""} />{" "}
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={feed?.lt}
          gameType={gameName}
          cardsOverlay={feed?.card != null ? <Lucky7CardsOverlay card={feed.card} /> : null}
        />{" "}
        {/* Reference renders .casino-detail once the feed has markets (mainData.length) */}
        {sub.length > 0 ? (
          <div className="casino-detail">
            <BetTableLucky7 sub={sub} onBetClick={handleBetClick} exposures={exposures} />{" "}
            {feed?.remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
                </div>{" "}
                <marquee>{feed.remark}</marquee>
              </div>
            ) : null}{" "}
            <CasinoLastResult
              gameType={gameName}
              data={resultData}
              gameData={gameData}
              viewAllLink={`/admin/reports/casinoresult/${gameName}`}
              resultComponent={<Lucky7RefResults gameType={gameName} data={resultData} variant="lucky7" fallbackTitle={title} />}
            />
          </div>
        ) : null}
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}

export default function Lucky7() {
  return <Lucky7Page defaultType="lucky7" />;
}
